// client/src/components/StandingsPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaTrophy, FaMedal, FaFutbol, FaArrowLeft, FaSyncAlt, FaChartLine, FaGlobe, FaCrown, FaStar } from 'react-icons/fa';
import { fetchStandings } from '../services/api';

const StandingsPage = ({ onBack, userContinent }) => {
  const { t, i18n } = useTranslation();
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCompetition, setActiveCompetition] = useState('domestic');
  const [selectedContinent, setSelectedContinent] = useState(userContinent || 'Europe');

  const loadStandings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const continent = activeCompetition === 'domestic' ? selectedContinent : null;
      const res = await fetchStandings(activeCompetition, continent);
      // تأكد من أن كل صف يحتوي على خاصية displayName (team_name إذا وجد، وإلا username)
      const standingsWithDisplay = res.data.map(row => ({
        ...row,
        displayName: row.team_name || row.username
      }));
      setStandings(standingsWithDisplay);
    } catch (err) {
      console.error(err);
      setError(t('standings.error'));
    } finally {
      setLoading(false);
    }
  }, [activeCompetition, selectedContinent, t]);

  useEffect(() => {
    loadStandings();
  }, [loadStandings]);

  const getQualificationStyle = (idx) => {
    if (activeCompetition !== 'domestic') return null;
    if (idx < 20) {
      return {
        bg: 'bg-gradient-to-r from-yellow-500/10 to-yellow-600/5',
        border: 'border-yellow-500/50',
        shadow: 'shadow-yellow-500/10',
        icon: <FaCrown className="text-yellow-400" />,
        label: t('standings.championsZone'),
        labelColor: 'text-yellow-400'
      };
    } else if (idx < 40) {
      return {
        bg: 'bg-gradient-to-r from-blue-500/10 to-blue-600/5',
        border: 'border-blue-500/50',
        shadow: 'shadow-blue-500/10',
        icon: <FaStar className="text-blue-400" />,
        label: t('standings.europaZone'),
        labelColor: 'text-blue-400'
      };
    }
    return null;
  };

  const getRankStyle = (idx) => {
    const top3 = [
      { border: 'border-yellow-500/70', glow: 'shadow-[0_0_20px_rgba(234,179,8,0.3)]', icon: <FaTrophy className="text-yellow-400 text-xl" />, bg: 'bg-gradient-to-r from-yellow-500/20 to-transparent' },
      { border: 'border-slate-300/70', glow: 'shadow-[0_0_15px_rgba(203,213,225,0.3)]', icon: <FaMedal className="text-slate-300 text-xl" />, bg: 'bg-gradient-to-r from-slate-500/20 to-transparent' },
      { border: 'border-amber-700/70', glow: 'shadow-[0_0_15px_rgba(180,83,9,0.3)]', icon: <FaMedal className="text-amber-600 text-xl" />, bg: 'bg-gradient-to-r from-amber-700/20 to-transparent' }
    ];
    if (idx < 3) return top3[idx];
    return { border: 'border-white/10', glow: '', icon: null, bg: '' };
  };

  const competitions = [
    { id: 'domestic', name: t('competition.domestic'), icon: <FaGlobe className="text-green-400" />, color: 'green' },
    { id: 'champions', name: t('competition.champions'), icon: <FaTrophy className="text-yellow-400" />, color: 'yellow' },
    { id: 'europa', name: t('competition.europa'), icon: <FaMedal className="text-blue-400" />, color: 'blue' }
  ];

  const continents = ['Europe', 'Asia', 'Africa', 'Americas'];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center" dir={i18n.dir()}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
        >
          <FaFutbol className="text-5xl text-green-500" />
        </motion.div>
        <p className="mt-4 text-green-400 font-mono tracking-widest animate-pulse">{t('standings.loading')}</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#0a0f0a] to-[#050805] text-white overflow-hidden font-sans" dir={i18n.dir()}>
      {/* Animated background grid */}
      <div className="absolute inset-0 z-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,255,0,0.1)_1px,transparent_1px)] bg-[size:30px_30px]" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <header className="flex flex-col md:flex-row justify-between items-center mb-10 gap-6">
          <motion.button
            whileHover={{ scale: 1.05, x: -5 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="group flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 px-5 py-2.5 rounded-2xl hover:bg-white/10 transition-all"
          >
            <FaArrowLeft className="group-hover:text-green-400 transition" />
            <span>{t('common.back')}</span>
          </motion.button>

          <div className="text-center">
            <motion.h1
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-4xl md:text-6xl font-black italic tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-white via-green-400 to-gray-500 uppercase"
            >
              {t('standings.title')}
            </motion.h1>
            <p className="text-green-500/60 text-sm font-mono tracking-[0.3em]">{t('standings.subtitle')}</p>
          </div>

          <motion.button
            whileHover={{ rotate: 180 }}
            onClick={loadStandings}
            className="p-4 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 hover:bg-green-500/20 transition-all"
          >
            <FaSyncAlt />
          </motion.button>
        </header>

        {/* Competition Tabs */}
        <div className="flex justify-center gap-4 mb-8 flex-wrap">
          {competitions.map((comp) => (
            <motion.button
              key={comp.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setActiveCompetition(comp.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                activeCompetition === comp.id
                  ? `bg-gradient-to-r from-${comp.color}-600 to-${comp.color}-500 text-black shadow-lg`
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            >
              {comp.icon}
              {comp.name}
            </motion.button>
          ))}
        </div>

        {/* Continent Selector */}
        {activeCompetition === 'domestic' && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="flex justify-center gap-3 mb-8 flex-wrap">
            {continents.map((cont) => (
              <button
                key={cont}
                onClick={() => setSelectedContinent(cont)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedContinent === cont
                    ? 'bg-green-600 text-white shadow-md scale-105'
                    : 'bg-black/40 text-gray-300 hover:bg-black/60 hover:scale-105'
                }`}
              >
                {cont === 'Europe' && '🇪🇺 Europe'}
                {cont === 'Asia' && '🇦🇸 Asia'}
                {cont === 'Africa' && '🇦🇫 Africa'}
                {cont === 'Americas' && '🌎 Americas'}
              </button>
            ))}
          </motion.div>
        )}

        {error && (
          <div className="text-center text-red-400 mb-6 p-4 bg-red-500/10 rounded-xl animate-pulse">
            {error}
          </div>
        )}

        {/* Desktop header */}
        <div className="hidden md:grid grid-cols-12 px-8 py-4 text-xs font-bold text-gray-500 uppercase tracking-widest border-b border-white/10">
          <div className="col-span-1">{t('standings.rank')}</div>
          <div className="col-span-4">{t('standings.contender')}</div>
          <div className="col-span-1 text-center text-green-400">{t('standings.pts')}</div>
          <div className="col-span-2 text-center text-gray-400">{t('standings.record')}</div>
          <div className="col-span-2 text-center text-gray-400">{t('standings.goals')}</div>
          <div className="col-span-2 text-center">{t('standings.diff')}</div>
        </div>

        {/* Standings list */}
        <div className="space-y-3">
          <AnimatePresence>
            {standings.map((row, idx) => {
              const rankStyle = getRankStyle(idx);
              const qualStyle = getQualificationStyle(idx);
              const gd = row.goals_for - row.goals_against;
              const isQualified = qualStyle !== null;

              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03, type: 'spring', stiffness: 200 }}
                  whileHover={{ scale: 1.01, backgroundColor: 'rgba(255,255,255,0.05)', transition: { duration: 0.2 } }}
                  className={`relative grid grid-cols-12 items-center px-4 md:px-8 py-4 backdrop-blur-xl border rounded-2xl transition-all duration-300 group ${
                    rankStyle.border
                  } ${rankStyle.glow} ${isQualified ? qualStyle.bg : rankStyle.bg} ${
                    isQualified ? `hover:shadow-lg ${qualStyle.shadow}` : ''
                  }`}
                  style={{ borderLeftWidth: isQualified ? '4px' : '1px', borderLeftColor: isQualified ? (idx < 20 ? '#FBBF24' : '#3B82F6') : 'rgba(255,255,255,0.1)' }}
                >
                  <div className="col-span-2 md:col-span-1 flex items-center gap-2">
                    {rankStyle.icon ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xl font-black italic text-gray-400">{idx + 1}</span>
                        {rankStyle.icon}
                      </div>
                    ) : (
                      <span className="text-xl font-black italic text-gray-500 group-hover:text-green-400 transition">
                        #{idx + 1}
                      </span>
                    )}
                    {isQualified && (
                      <div className="hidden md:block ml-1" title={qualStyle.label}>
                        {qualStyle.icon}
                      </div>
                    )}
                  </div>

                  <div className="col-span-7 md:col-span-4">
                    <div className="font-bold text-lg tracking-tight group-hover:translate-x-2 transition-transform">
                      {/* استخدام displayName (team_name أو username) */}
                      {row.displayName}
                      {isQualified && (
                        <span className={`ml-2 text-[10px] font-mono ${qualStyle.labelColor} hidden md:inline`}>
                          ({qualStyle.label})
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="col-span-3 md:col-span-1 text-right md:text-center">
                    <span className={`text-2xl font-black ${idx < 3 ? 'text-yellow-400' : 'text-green-400'}`}>
                      {row.points}
                    </span>
                  </div>

                  <div className="hidden md:flex col-span-2 justify-center gap-1 font-mono text-sm">
                    <span className="text-green-400">{row.wins}W</span>
                    <span className="text-gray-500">{row.draws}D</span>
                    <span className="text-red-400">{row.losses}L</span>
                  </div>

                  <div className="hidden md:flex col-span-2 justify-center gap-1 font-mono text-sm italic">
                    <span className="text-white">{row.goals_for}</span>
                    <span className="text-gray-600">/</span>
                    <span className="text-gray-400">{row.goals_against}</span>
                  </div>

                  <div className={`hidden md:block col-span-2 text-center font-bold ${gd >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    <span className="bg-black/40 px-3 py-1 rounded-lg border border-white/5 backdrop-blur-sm">
                      {gd > 0 ? `+${gd}` : gd}
                    </span>
                  </div>

                  {isQualified && (
                    <div className="absolute -top-2 -right-2 md:hidden">
                      <div className={`text-[8px] font-bold px-2 py-0.5 rounded-full ${idx < 20 ? 'bg-yellow-500 text-black' : 'bg-blue-500 text-white'}`}>
                        {idx < 20 ? 'CL' : 'EL'}
                      </div>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Qualification Legend */}
        {activeCompetition === 'domestic' && standings.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-10 p-5 rounded-2xl bg-gradient-to-r from-gray-900/50 to-gray-800/30 backdrop-blur-sm border border-white/10"
          >
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-yellow-500 rounded-full shadow-md"></div>
                <span className="text-gray-300">{t('standings.championsLegend')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-blue-500 rounded-full shadow-md"></div>
                <span className="text-gray-300">{t('standings.europaLegend')}</span>
              </div>
              <div className="flex items-center gap-2">
                <FaTrophy className="text-yellow-400 text-sm" />
                <span className="text-gray-300">{t('standings.top3Legend')}</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Footer Stats */}
        <footer className="mt-12 p-6 rounded-3xl bg-green-500/5 border border-green-500/10 backdrop-blur-sm">
          <div className="flex flex-wrap justify-center gap-8 opacity-60 hover:opacity-100 transition-opacity">
            <div className="flex items-center gap-2 text-xs uppercase tracking-tighter">
              <FaChartLine className="text-green-400" />
              <span>{t('standings.liveStats')}</span>
            </div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-tighter">
              <div className="w-2 h-2 rounded-full bg-yellow-400 animate-pulse" />
              <span>{t('standings.championsZone')}</span>
            </div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-tighter">
              <div className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              <span>{t('standings.europaZone')}</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StandingsPage;